import styled from 'styled-components';
import type { ArtworkFilters as ArtworkFiltersType } from '../../services/artworkService';

interface Student {
  student_id: number;
  name: string;
}

interface Tag {
  tag_id: number;
  tag_name: string;
}

interface ArtworkFiltersProps {
  filters: ArtworkFiltersType;
  searchQuery: string;
  students: Student[];
  tags: Tag[];
  hasFilters: boolean;
  onSearchChange: (value: string) => void;
  onFilterChange: (key: keyof ArtworkFiltersType, value: ArtworkFiltersType[keyof ArtworkFiltersType]) => void;
  onClearFilters: () => void;
}

const FiltersSection = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: ${({ theme }) => theme.spacing.lg};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const FilterControl = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs};
`;

const FilterLabel = styled.label`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

const FilterInput = styled.input`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 2px solid ${({ theme }) => theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const FilterSelect = styled.select`
  padding: ${({ theme }) => theme.spacing.sm};
  border: 2px solid ${({ theme }) => theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ClearButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.white};
  border: none;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};

  &:hover {
    background: ${({ theme }) => theme.colors.primary}dd;
  }
`;

function getStudentName(student: Student): string {
  return student.name || 'Unknown Student';
}

export function ArtworkFilters({
  filters,
  searchQuery,
  students,
  tags,
  hasFilters,
  onSearchChange,
  onFilterChange,
  onClearFilters,
}: ArtworkFiltersProps) {
  return (
    <FiltersSection>
      <FiltersGrid>
        <FilterControl>
          <FilterLabel>Search</FilterLabel>
          <FilterInput
            type="text"
            placeholder="Search by student name..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </FilterControl>

        <FilterControl>
          <FilterLabel>Student</FilterLabel>
          <FilterSelect
            value={filters.student_id || ''}
            onChange={(e) => onFilterChange('student_id', e.target.value ? parseInt(e.target.value) : undefined)}
          >
            <option value="">All Students</option>
            {students.map(student => (
              <option key={student.student_id} value={student.student_id}>
                {getStudentName(student)}
              </option>
            ))}
          </FilterSelect>
        </FilterControl>

        <FilterControl>
          <FilterLabel>Tags</FilterLabel>
          <FilterSelect
            value={filters.tags?.[0] || ''}
            onChange={(e) => onFilterChange('tags', e.target.value ? [e.target.value] : undefined)}
          >
            <option value="">All Tags</option>
            {tags.map(tag => (
              <option key={tag.tag_id} value={tag.tag_name}>
                {tag.tag_name}
              </option>
            ))}
          </FilterSelect>
        </FilterControl>

        <FilterControl>
          <FilterLabel>Date Range</FilterLabel>
          <FilterInput
            type="date"
            value={filters.uploaded_after || ''}
            onChange={(e) => onFilterChange('uploaded_after', e.target.value)}
          />
        </FilterControl>
      </FiltersGrid>

      {hasFilters && (
        <ClearButton onClick={onClearFilters}>
          Clear Filters
        </ClearButton>
      )}
    </FiltersSection>
  );
}

export type { ArtworkFiltersProps };
